import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { startLeagueOAuthClaim, claimLeaguePassword, getLeagueDetails } from '../services/api';
import '../styles/fonts.css';

function ClaimLeague() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const claimToken = searchParams.get('claim_token');
    const oauthError = searchParams.get('error');

    const [league, setLeague] = useState(null);
    const [loading, setLoading] = useState(true);
    const [oauthLoading, setOauthLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [success, setSuccess] = useState(false);

    const ERROR_MESSAGES = {
        nao_e_dono: t('claimLeague.wrongAccount'),
        falha_token: t('claimLeague.oauthError'),
        falha_userinfo: t('claimLeague.userInfoError'),
        estado_invalido: t('claimLeague.sessionExpired'),
        params_invalidos: t('claimLeague.invalidParams'),
    };

    useEffect(() => {
        getLeagueDetails(id)
            .then(data => setLeague(data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [id]);

    const handleOAuthStart = async () => {
        setOauthLoading(true);
        try {
            const data = await startLeagueOAuthClaim(id);
            window.location.href = data.oauth_url;
        } catch {
            setOauthLoading(false);
        }
    };

    const handleSetPassword = async (e) => {
        e.preventDefault();
        setSaveError('');
        if (password !== confirmPassword) {
            setSaveError(t('claimLeague.passwordMismatch'));
            return;
        }
        if (password.length < 6) {
            setSaveError(t('claimLeague.passwordTooShort'));
            return;
        }
        setSaving(true);
        try {
            await claimLeaguePassword(id, claimToken, password);
            setSuccess(true);
        } catch (err) {
            setSaveError(err?.error || t('claimLeague.saveError'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div>
            <Header />
            <main style={{ color: 'white', textAlign: 'center', padding: '60px' }}>{t('common.loading')}</main>
            <Footer />
        </div>
    );

    return (
        <div>
            <Header />
            <main style={{ maxWidth: '560px', margin: '40px auto', padding: '0 16px' }}>

                <div style={{ marginBottom: '20px', fontSize: '13px', color: '#9ba3ab' }}>
                    <Link to="/leagues" style={{ color: '#9ba3ab', textDecoration: 'none' }}>{t('common.leagues')}</Link>
                    {' > '}
                    {league && (
                        <>
                            <Link to={`/leagues/${id}`} style={{ color: '#9ba3ab', textDecoration: 'none' }}>
                                {league.name}
                            </Link>
                            {' > '}
                        </>
                    )}
                    <span style={{ color: '#fff' }}>{t('claimLeague.takeOver')}</span>
                </div>

                <div style={{
                    background: '#1a1d29',
                    border: '1px solid #2d313f',
                    borderRadius: '10px',
                    padding: '32px',
                }}>
                    <h2 style={{ color: '#fff', marginBottom: '8px', fontSize: '20px' }}>
                        {t('claimLeague.title')}
                    </h2>
                    {league && (
                        <p style={{ color: '#9ba3ab', marginBottom: '24px', fontSize: '14px' }}>
                            {league.name}
                        </p>
                    )}

                    {/* OAuth error returned by backend */}
                    {oauthError && !claimToken && (
                        <div style={{
                            background: '#2a1515', border: '1px solid #ff4747',
                            borderRadius: '6px', padding: '12px 16px', marginBottom: '20px',
                            color: '#ff7070', fontSize: '14px',
                        }}>
                            {ERROR_MESSAGES[oauthError] || t('claimLeague.genericError')}
                        </div>
                    )}

                    {/* STEP 1: Verify with iRacing (no claim_token) */}
                    {!claimToken && !success && (
                        <>
                            <p style={{ color: '#c0c8d4', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>
                                {t('claimLeague.verifyDescription')}
                            </p>
                            <p style={{ color: '#9ba3ab', fontSize: '13px', marginBottom: '24px' }}>
                                {t('claimLeague.ownerOnly')}
                            </p>
                            <button
                                onClick={handleOAuthStart}
                                disabled={oauthLoading}
                                style={{
                                    width: '100%', padding: '12px',
                                    background: oauthLoading ? '#555' : '#0056bd',
                                    color: '#fff', border: 'none', borderRadius: '6px',
                                    fontSize: '15px', fontWeight: '600', cursor: oauthLoading ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {oauthLoading ? t('claimLeague.redirecting') : t('claimLeague.verifyWithIRacing')}
                            </button>
                        </>
                    )}

                    {/* STEP 2: Set password (with valid claim_token) */}
                    {claimToken && !success && (
                        <>
                            <div style={{
                                background: '#0f2a14', border: '1px solid #2d6b35',
                                borderRadius: '6px', padding: '12px 16px', marginBottom: '24px',
                                color: '#5cc96e', fontSize: '14px',
                            }}>
                                {t('claimLeague.identityVerified')}
                            </div>
                            <form onSubmit={handleSetPassword}>
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', color: '#c0c8d4', marginBottom: '6px', fontSize: '14px' }}>
                                        {t('claimLeague.newPassword')}
                                    </label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        placeholder={t('claimLeague.minChars')}
                                        style={{
                                            width: '100%', padding: '10px 12px',
                                            background: '#0f111a', border: '1px solid #2d313f',
                                            borderRadius: '6px', color: '#fff', fontSize: '14px',
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                </div>
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', color: '#c0c8d4', marginBottom: '6px', fontSize: '14px' }}>
                                        {t('claimLeague.confirmPassword')}
                                    </label>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                        placeholder={t('claimLeague.repeatPassword')}
                                        style={{
                                            width: '100%', padding: '10px 12px',
                                            background: '#0f111a', border: '1px solid #2d313f',
                                            borderRadius: '6px', color: '#fff', fontSize: '14px',
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                </div>
                                {saveError && (
                                    <p style={{ color: '#ff7070', fontSize: '13px', marginBottom: '12px' }}>{saveError}</p>
                                )}
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{
                                        width: '100%', padding: '12px',
                                        background: saving ? '#555' : '#0056bd',
                                        color: '#fff', border: 'none', borderRadius: '6px',
                                        fontSize: '15px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {saving ? t('common.saving') : t('claimLeague.setPassword')}
                                </button>
                            </form>
                        </>
                    )}

                    {/* STEP 3: Success */}
                    {success && (
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
                            <p style={{ color: '#5cc96e', fontSize: '16px', marginBottom: '8px', fontWeight: '600' }}>
                                {t('claimLeague.successTitle')}
                            </p>
                            <p style={{ color: '#9ba3ab', fontSize: '14px', marginBottom: '24px' }}>
                                {t('claimLeague.successMessage')}
                            </p>
                            <button
                                onClick={() => navigate(`/leagues/${id}/organizer`)}
                                style={{
                                    padding: '10px 24px', background: '#0056bd',
                                    color: '#fff', border: 'none', borderRadius: '6px',
                                    fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                                }}
                            >
                                {t('claimLeague.goToPanel')}
                            </button>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}

export default ClaimLeague;
